import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { 
  Box, 
  Typography, 
  Grid,
  Card,
  CardContent,
  Chip, 
  TextField, 
  Dialog, 
  Button,
  DialogContent, 
  DialogTitle,
  DialogActions,
  IconButton,
  Skeleton,
  Stack,
  MenuItem,
  Select,
  FormControl,
  styled,
  InputLabel,
  Paper,
  Avatar,
  useTheme,
  alpha,
  Tooltip,
  Fade,
  Zoom,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  AttachMoney, 
  CheckCircle, 
  Schedule, 
  ErrorOutline, 
  Paid,
  TrendingUp,
  Close,
  Save,
  Cancel,
  Refresh,
  Receipt,
  Block,
  Download,
  Share,
  DateRange,
  Search as SearchIcon,
  TrendingDown,
  Info
} from '@mui/icons-material';
import { format, subDays, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';

const db = getFirestore();

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Styled Components - Fixed version
const GradientCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none'
  }
}));

// Fixed StatusChip - Removed the status prop from being passed to the DOM
const StatusChip = ({ status, ...props }) => {
  const theme = useTheme();
  const colors = {
    'Validé': { bg: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main, icon: <CheckCircle /> },
    'En attente': { bg: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main, icon: <Schedule /> },
    'Rejeté': { bg: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, icon: <Block /> }
  };
  const config = colors[status] || colors['En attente'];
  
  return (
    <Chip
      icon={config.icon}
      label={status}
      size="small"
      sx={{
        bgcolor: config.bg,
        color: config.color,
        fontWeight: 600,
        '& .MuiChip-icon': { color: config.color }
      }}
      {...props}
    />
  );
};

const StatCard = ({ title, value, icon: Icon, color, subtitle, trend, loading }) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="text" width="60%" height={32} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="40%" height={20} />
      </Paper>
    );
  }
  
  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(color, 0.02)} 100%)`,
          border: `1px solid ${alpha(color, 0.15)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: alpha(color, 0.3),
            boxShadow: `0 8px 24px ${alpha(color, 0.15)}`
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 44, height: 44 }}>
            <Icon />
          </Avatar>
          {trend !== undefined && trend !== null && (
            <Chip
              size="small"
              icon={trend >= 0 ? <TrendingUp sx={{ fontSize: 12 }} /> : <TrendingDown sx={{ fontSize: 12 }} />}
              label={`${Math.abs(trend)}%`}
              sx={{
                bgcolor: alpha(trend >= 0 ? '#10b981' : '#ef4444', 0.1),
                color: trend >= 0 ? '#10b981' : '#ef4444',
                height: 24,
                fontSize: '0.7rem'
              }}
            />
          )}
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </Paper>
    </motion.div>
  );
};

const MontantDateStatueList = ({ partenaireId, restaurantId }) => {
  const theme = useTheme();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEntry, setEditEntry] = useState(null);
  const [filters, setFilters] = useState({ date: 'all', status: 'all', search: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!partenaireId) {
        console.log('No partenaireId provided');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching MontantDateStatue for partenaireId:', partenaireId);
        
        // Get all documents from MontantDateStatue collection
        const querySnapshot = await getDocs(collection(db, 'MontantDateStatue'));
        console.log('Total MontantDateStatue documents found:', querySnapshot.docs.length);
        
        // Filter documents by PartenaireId
        const data = [];
        querySnapshot.forEach(doc => {
          const docData = doc.data();
          
          // Check if this document belongs to the current partner
          const docPartenaireId = docData.PartenaireId || docData.partenaireId || docData.partnerId || docData.partner_id;
          
          if (docPartenaireId === partenaireId) {
            data.push({
              id: doc.id,
              ...docData,
              MontantRecu: docData.MontantRecu || docData.montantRecu || docData.amount || 0,
              Statue: docData.Statue || docData.statue || docData.status || 'En attente',
              DateDebut: docData.DateDebut || docData.dateDebut || docData.startDate,
              DateFin: docData.DateFin || docData.dateFin || docData.endDate,
              createdAt: docData.createdAt || docData.DateDebut || new Date().toISOString()
            });
          }
        });
        
        // Sort by date (newest first)
        data.sort((a, b) => {
          const dateA = a.DateDebut ? new Date(a.DateDebut) : new Date(0);
          const dateB = b.DateDebut ? new Date(b.DateDebut) : new Date(0);
          return dateB - dateA;
        });
        
        console.log('Filtered entries for partner:', data.length);
        setEntries(data);
      } catch (error) {
        console.error('Error fetching MontantDateStatue:', error);
        showSnackbar('Erreur lors du chargement des données: ' + error.message, 'error');
      } finally {
        setLoading(false);
        setStatsLoading(false);
      }
    };
    
    fetchData();
  }, [partenaireId]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    let filtered = [...entries];
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(entry => {
        const montantRecu = entry.MontantRecu?.toString() || '';
        return montantRecu.includes(searchLower);
      });
    }
    
    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(entry => entry.Statue === filters.status);
    }
    
    // Date filter
    if (filters.date !== 'all' && filtered.length > 0) {
      const now = new Date();
      const startDate = filters.date === 'week' ? subDays(now, 7) : subMonths(now, 1);
      filtered = filtered.filter(entry => {
        const entryDate = entry.DateDebut ? new Date(entry.DateDebut) : null;
        return entryDate && entryDate >= startDate;
      });
    }
    
    return filtered;
  }, [entries, filters]);

  // Pagination
  const paginatedEntries = filteredEntries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Summary calculations
  const summary = useMemo(() => {
    const totals = entries.reduce((acc, entry) => {
      const amount = parseFloat(entry.MontantRecu) || 0;
      const status = entry.Statue;
      acc.total += amount;
      if (status === 'Validé') acc.valid += amount;
      if (status === 'En attente') acc.pending += amount;
      if (status === 'Rejeté') acc.rejected += amount;
      return acc;
    }, { total: 0, valid: 0, pending: 0, rejected: 0 });
    
    return totals;
  }, [entries]);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar(prev => ({ ...prev, open: false })), 3000);
  };

  const handleSave = async (updatedData) => {
    try {
      const entryRef = doc(db, 'MontantDateStatue', updatedData.id);
      await updateDoc(entryRef, {
        MontantRecu: parseFloat(updatedData.MontantRecu),
        Statue: updatedData.Statue,
        updatedAt: new Date().toISOString()
      });
      setEntries(entries.map(entry => 
        entry.id === updatedData.id ? { ...entry, MontantRecu: parseFloat(updatedData.MontantRecu), Statue: updatedData.Statue } : entry
      ));
      setEditEntry(null);
      showSnackbar('Transaction modifiée avec succès', 'success');
    } catch (err) {
      console.error('Error updating entry:', err);
      showSnackbar('Erreur de mise à jour: ' + err.message, 'error');
    }
  };

  const deleteEntry = async (id) => {
    try {
      await deleteDoc(doc(db, 'MontantDateStatue', id));
      setEntries(entries.filter(e => e.id !== id));
      setDeleteConfirm(null);
      showSnackbar('Transaction supprimée avec succès', 'success');
    } catch (err) {
      console.error('Error deleting entry:', err);
      showSnackbar('Erreur de suppression: ' + err.message, 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return numAmount.toLocaleString('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }) + ' TND';
  };

  const calculateDaysDifference = (start, end) => {
    if (!start || !end) return 0;
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diff = endDate - startDate;
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    } catch {
      return 0;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={12} key={i}>
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with Gradient Banner */}
      <GradientCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <Receipt sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Transactions Financières
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {entries.length} transaction{entries.length > 1 ? 's' : ''} · Dernière mise à jour: {format(new Date(), 'dd MMM yyyy', { locale: fr })}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Rafraîchir">
                <IconButton 
                  onClick={() => window.location.reload()}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Exporter">
                <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <Download />
                </IconButton>
              </Tooltip>
              <Tooltip title="Partager">
                <IconButton sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <Share />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </GradientCard>

      {/* Statistics Cards */}
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Reçu"
              value={formatAmount(summary.total)}
              icon={Paid}
              color="#3b82f6"
              loading={statsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Montant Validé"
              value={formatAmount(summary.valid)}
              icon={CheckCircle}
              color="#10b981"
              loading={statsLoading}
              trend={summary.valid > 0 ? 12 : 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="En Attente"
              value={formatAmount(summary.pending)}
              icon={Schedule}
              color="#f59e0b"
              loading={statsLoading}
              trend={summary.pending > 0 ? -5 : 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Rejeté"
              value={formatAmount(summary.rejected)}
              icon={Block}
              color="#ef4444"
              loading={statsLoading}
            />
          </Grid>
        </Grid>
      </motion.div>

      {/* Filters Section */}
      <motion.div variants={fadeInUp}>
        <Paper sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Rechercher par montant..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 0 })}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: filters.search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setFilters({ ...filters, search: '', page: 0 })}>
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 0 })}
                  label="Statut"
                >
                  <MenuItem value="all">Tous les statuts</MenuItem>
                  <MenuItem value="Validé">Validé</MenuItem>
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Rejeté">Rejeté</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Période</InputLabel>
                <Select
                  value={filters.date}
                  onChange={(e) => setFilters({ ...filters, date: e.target.value, page: 0 })}
                  label="Période"
                >
                  <MenuItem value="all">Toutes les dates</MenuItem>
                  <MenuItem value="week">7 derniers jours</MenuItem>
                  <MenuItem value="month">30 derniers jours</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({ date: 'all', status: 'all', search: '', page: 0 })}
                startIcon={<Refresh />}
                size="small"
              >
                Réinitialiser
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Transactions Table */}
      <motion.div variants={fadeInUp}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt fontSize="small" />
              Liste des transactions
            </Typography>
            <Chip 
              label={`${filteredEntries.length} transaction${filteredEntries.length > 1 ? 's' : ''}`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>
          
          {filteredEntries.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Info sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune transaction trouvée
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filters.search || filters.status !== 'all' || filters.date !== 'all' 
                  ? 'Essayez de modifier vos filtres' 
                  : 'Aucune transaction enregistrée pour ce partenaire'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Période</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Durée</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Montant</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date d'enregistrement</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence>
                      {paginatedEntries.map((entry, index) => (
                        <motion.tr
                          key={entry.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DateRange fontSize="small" color="action" />
                              <Box>
                                <Typography variant="body2">
                                  {formatDate(entry.DateDebut)} - {formatDate(entry.DateFin)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${calculateDaysDifference(entry.DateDebut, entry.DateFin)} jours`}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" fontWeight={600} sx={{ color: '#10b981' }}>
                              {formatAmount(entry.MontantRecu)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={entry.Statue} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(entry.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title="Modifier">
                                <IconButton 
                                  size="small" 
                                  onClick={() => setEditEntry(entry)}
                                  sx={{ color: theme.palette.primary.main }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton 
                                  size="small" 
                                  onClick={() => setDeleteConfirm(entry)}
                                  sx={{ color: theme.palette.error.main }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredEntries.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Lignes par page"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
              />
            </>
          )}
        </Paper>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog 
        open={!!editEntry} 
        onClose={() => setEditEntry(null)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <Box sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          p: 2.5,
          color: 'white'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit fontSize="small" />
            Modifier la transaction
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
            Période: {formatDate(editEntry?.DateDebut)} - {formatDate(editEntry?.DateFin)}
          </Typography>
        </Box>
        
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Montant Reçu"
                fullWidth
                type="number"
                value={editEntry?.MontantRecu || ''}
                onChange={(e) => setEditEntry({ ...editEntry, MontantRecu: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>,
                }}
                inputProps={{ step: 0.001 }}
                helperText="Saisissez le montant en TND"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={editEntry?.Statue || ''}
                  onChange={(e) => setEditEntry({ ...editEntry, Statue: e.target.value })}
                  label="Statut"
                >
                  <MenuItem value="Validé">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle fontSize="small" sx={{ color: '#10b981' }} />
                      Validé
                    </Box>
                  </MenuItem>
                  <MenuItem value="En attente">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule fontSize="small" sx={{ color: '#f59e0b' }} />
                      En attente
                    </Box>
                  </MenuItem>
                  <MenuItem value="Rejeté">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Block fontSize="small" sx={{ color: '#ef4444' }} />
                      Rejeté
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={() => setEditEntry(null)} startIcon={<Cancel />}>
            Annuler
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleSave(editEntry)}
            startIcon={<Save />}
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: { borderRadius: 3, width: 400, textAlign: 'center' }
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Avatar sx={{ 
            width: 70, 
            height: 70, 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            margin: '0 auto 16px'
          }}>
            <ErrorOutline sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Confirmer la suppression
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir supprimer cette transaction ?
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 3, color: theme.palette.error.main }}>
            {deleteConfirm && formatAmount(deleteConfirm.MontantRecu)}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button 
              variant="outlined" 
              onClick={() => setDeleteConfirm(null)}
              startIcon={<Cancel />}
            >
              Annuler
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={() => deleteEntry(deleteConfirm.id)}
              startIcon={<Delete />}
            >
              Supprimer
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert 
          severity={snackbar.severity} 
          sx={{ borderRadius: 2 }}
          icon={snackbar.severity === 'success' ? <CheckCircle /> : <ErrorOutline />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MontantDateStatueList;